import { translateVisibleNode, useI18n, useVisibleCopy } from '../i18n';

export type StatusTone = 'green' | 'blue' | 'yellow' | 'red' | 'muted';

export type StatusItem = {
  label: string;
  value: string;
  evidence: string;
  tone?: StatusTone;
};

export function ResourceHeader({
  eyebrow,
  title,
  description,
  meta,
  actions,
}: {
  eyebrow: string;
  title: string;
  description: string;
  meta: Array<[string, string]>;
  actions?: React.ReactNode;
}) {
  const ui = useVisibleCopy();

  return (
    <header className="resource-header">
      <div>
        <p className="eyebrow">{ui(eyebrow)}</p>
        <h1>{ui(title)}</h1>
        <p className="resource-description">{ui(description)}</p>
        <dl className="resource-meta">
          {meta.map(([key, value]) => (
            <div key={key}>
              <dt>{ui(key)}</dt>
              <dd>{ui(value)}</dd>
            </div>
          ))}
        </dl>
      </div>
      {actions ? <div className="resource-actions">{actions}</div> : null}
    </header>
  );
}

export function StatusStrip({ items }: { items: StatusItem[] }) {
  const { locale, t } = useI18n();

  return (
    <section className="status-strip" aria-label={t('resource.statusAriaLabel')}>
      {items.map(item => (
        <div key={item.label} className={`status-item tone-${item.tone ?? 'muted'}`}>
          <span>{translateVisibleNode(item.label, locale)}</span>
          <strong>{translateVisibleNode(item.value, locale)}</strong>
          <small>{translateVisibleNode(item.evidence, locale)}</small>
        </div>
      ))}
    </section>
  );
}

export function Tabs({
  tabs,
  activeId,
  onSelect,
}: {
  tabs: Array<{ id: string; label: string }>;
  activeId: string;
  onSelect?: (id: string) => void;
}) {
  const ui = useVisibleCopy();

  return (
    <div className="tabs" role="tablist">
      {tabs.map(tab => (
        <button
          key={tab.id}
          aria-selected={tab.id === activeId}
          className={tab.id === activeId ? 'tab active' : 'tab'}
          role="tab"
          type="button"
          onClick={() => onSelect?.(tab.id)}
        >
          {ui(tab.label)}
        </button>
      ))}
    </div>
  );
}

export function PropertyTable({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  const { locale } = useI18n();

  return (
    <dl className="property-table">
      {rows.map(([key, value]) => (
        <div key={key}>
          <dt>{translateVisibleNode(key, locale)}</dt>
          <dd>{translateVisibleNode(value, locale)}</dd>
        </div>
      ))}
    </dl>
  );
}
